// BillingGate.jsx
//
// Two-part billing UX:
//   1. Persistent banner across the top of the app for non-comped tenants
//      without a card on file ("Add a card to enable calls/SMS/AI").
//   2. Hard-gate modal that pops when an API call returns 402 CARD_REQUIRED.
//      Dispatches a window event from the axios interceptor.
//
// Both surfaces deep-link to /billing where the agent hits the "Add card"
// button to be redirected into Stripe Checkout (setup mode).
import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CreditCard, X, AlertTriangle } from 'lucide-react';
import { apiGet, apiPost } from '../lib/api.js';
import { useAuth } from '../store/auth.js';

export default function BillingGate() {
  const isAuthed = useAuth((s) => !!s.token);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [bannerHidden, setBannerHidden] = useState(false);

  // Pull billing state. We poll lightly so the banner clears within ~30s of
  // a card being saved (the Stripe webhook updates the tenant doc).
  const { data, refetch } = useQuery({
    queryKey: ['billing-state'],
    queryFn: () => apiGet('/stripe/billing-state'),
    enabled: isAuthed,
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
  const card = data?.data || null;

  // Listen for the global "card required" event from the axios interceptor.
  useEffect(() => {
    function onEvt(e) {
      setModalMsg(e?.detail?.message || 'Add a card to continue.');
      setModalOpen(true);
    }
    window.addEventListener('civix:card-required', onEvt);
    return () => window.removeEventListener('civix:card-required', onEvt);
  }, []);

  async function startCardSetup() {
    setBusy(true);
    try {
      const r = await apiPost('/stripe/add-card', {});
      if (r?.data?.checkoutUrl) {
        window.location.href = r.data.checkoutUrl;
      } else {
        setBusy(false);
      }
    } catch (e) {
      setBusy(false);
    }
  }

  if (!isAuthed || !card) return null;

  // Banner: show whenever no card is on file. Comped tenants still see it
  // (soft nudge) — they can dismiss for the session.
  const showBanner = !card.hasCard && !bannerHidden;
  const isComped = !!card.isComped;

  return (
    <>
      {showBanner && (
        <div className={`w-full px-4 py-2 flex items-center gap-3 text-sm border-b ${
          isComped
            ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-100'
            : 'bg-amber-500/10 border-amber-500/30 text-amber-100'
        }`}>
          <CreditCard className="w-4 h-4 shrink-0" />
          <div className="flex-1">
            {isComped
              ? 'Add a card now so calls, SMS, and AI keep working when your beta access ends. No charge today.'
              : 'Add a card to enable calls, SMS, and AI usage.'}
          </div>
          <button
            onClick={startCardSetup}
            disabled={busy}
            className="px-3 py-1 rounded-sm bg-white text-black text-xs font-semibold hover:bg-white/90 disabled:opacity-50"
          >
            {busy ? 'Opening…' : 'Add card'}
          </button>
          {isComped && (
            <button
              onClick={() => setBannerHidden(true)}
              className="text-current/70 hover:text-current"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-100 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setModalOpen(false)}>
          <div className="bg-[#0a0a1f] border border-amber-500/40 rounded-xl p-6 max-w-md w-full shadow-2xl"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1">
                <div className="font-semibold text-white">Card required to continue</div>
                <div className="text-sm text-white/70 mt-1">{modalMsg}</div>
                <div className="text-xs text-white/50 mt-3">
                  Adding a card does not charge you. You're billed only for actual usage (Twilio, AI, lead purchases) at cost.
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setModalOpen(false)}
                className="px-3 py-2 text-sm rounded-sm border border-white/15 text-white/70 hover:bg-white/5"
              >
                Not now
              </button>
              <button
                onClick={startCardSetup}
                disabled={busy}
                className="px-4 py-2 text-sm rounded-sm bg-civix-purple hover:bg-civix-purple/90 text-white font-medium disabled:opacity-50 flex items-center gap-2"
              >
                <CreditCard className="w-4 h-4" />
                {busy ? 'Opening…' : 'Add card'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
